import dayjs, { Dayjs } from "dayjs";
import { Availability } from "lib/features/availability/types";
import { Item } from "lib/features/item/types";

export const getWeekdays = (format = "dd") => {
  const startOfWeek = dayjs().startOf("week");
  return Array.from({ length: 7 }).map((_, i) =>
    startOfWeek.add(i, "day").format(format),
  );
};

export function getCalendarDays(month: Dayjs) {
  const start = month.startOf("month").startOf("week");
  const end = month.endOf("month").endOf("week");

  const days: Dayjs[] = [];
  let current = start;

  while (current.isBefore(end) || current.isSame(end, "day")) {
    days.push(current);
    current = current.add(1, "day");
  }

  return days;
}

export type CalendarAvailability = {
  // i hate doing this, but Availability + FareharborAvailability
  // have such different structures
  availability: any;
  availabilityId: string; // unique ID for selection tracking
  item: {
    id: number;
    name?: string;
    capacity?: number;
    available?: number;
    itemType?: number;
  };
  dayKey: string; // 'YYYY-MM-DD'
  startTimeKey?: string; // 'HH:mm'
  isFareharbor?: boolean;
};

export function indexAvailabilitiesByDay(
  items: Item[],
  deletedFareharborIds?: Set<string>,
) {
  const byDay = new Map<string, CalendarAvailability[]>();

  for (const item of items) {
    // Skip items with record_status === false
    if (item.record_status === false) continue;

    // Handle FareHarbor items (item_type === 4)
    if (item.item_type === 4 && item.fareharbor_availabilities?.length) {
      for (const fhAvail of item.fareharbor_availabilities) {
        const fareharborId = String(fhAvail.pk ?? fhAvail.id);

        // Skip deleted FareHarbor availabilities
        if (deletedFareharborIds?.has(fareharborId)) continue;

        if (!fhAvail.start_at) continue;

        const dayKey = dayjs(fhAvail.start_at).format("YYYY-MM-DD");
        const startTimeKey = dayjs(fhAvail.start_at).isValid()
          ? dayjs(fhAvail.start_at).format("HH:mm")
          : undefined;

        const entry: CalendarAvailability = {
          availability: {
            ...fhAvail,
            date: new Date(dayKey),
            start_time: new Date(fhAvail.start_at),
            end_time: new Date(fhAvail.end_at),
            quantity: fhAvail.capacity,
            private: false,
            record_status: true,
          },
          availabilityId: fareharborId,
          item: {
            id: item.id,
            name: item.farehabor_item_details?.name,
            capacity: fhAvail.capacity,
            available: fhAvail.capacity,
            itemType: item.item_type,
          },
          dayKey,
          startTimeKey,
          isFareharbor: true,
        };

        const existing = byDay.get(dayKey);
        if (existing) existing.push(entry);
        else byDay.set(dayKey, [entry]);
      }
      continue;
    }

    // Handle regular availabilities
    for (const availability of item.availabilities ?? []) {
      // Skip availabilities with record_status === false
      if (availability.record_status === false) continue;

      if (!availability.start_time) continue;

      const dayKey = dayjs(availability.start_time).format("YYYY-MM-DD");

      const startTimeKey = availability.start_time
        ? dayjs(availability.start_time).isValid()
          ? dayjs(availability.start_time).format("HH:mm")
          : String(availability.start_time).slice(0, 5)
        : undefined;

      const entry: CalendarAvailability = {
        availability,
        availabilityId: String(availability.id),
        item: {
          id: item.id,
          name: item.itemDetails?.[0]?.item_name,
          capacity: item.capacity,
          available: item.available,
          itemType: item.item_type,
        },
        dayKey,
        startTimeKey,
        isFareharbor: false,
      };

      const existing = byDay.get(dayKey);
      if (existing) existing.push(entry);
      else byDay.set(dayKey, [entry]);
    }
  }

  return { byDay };
}
